import {
  useDeleteAssetsMutation,
  useAssetSearchMutation,
  useBulkDeleteAssetsMutation
} from '@ports/internal-services/asset/assetSlice';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedMedia } from '@ports/common/commonStateSlice';
import { BaseAppLayout } from 'src/layouts';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ConfirmationModal,
  SelectionBar
} from '@business/workflows/ComponentUI';
import { SVGAssets } from '@business/assets/vectors';
import { EmptyScreen } from '@business/workflows/ComponentUI/Dashboard';
import { ConfirmationBox } from '@business/workflows/ComponentUI/ConfirmationBox';
import { CreateOptions } from '@business/workflows/ComponentUI/Photos/CreateOptions';
import { getAlertState, resetAlertState } from '@ports/common/AlertStateSlice';
import { AlertProps } from '@business/interfaces/alert';
import { Alert } from '@business/workflows/ComponentUI/Alert';
import { useAddFavoritesMutation } from '@ports/internal-services/favorites/favoritesSlice';
import {
  useAddPrivateMutation,
  useBulkCreatePrivateMutation
} from '@ports/internal-services/private/privateSlice';
import {
  getSubscriberId,
  getUserNotificationData,
  setIsMediaUploadModalOpen
} from '@ports/common/sessionStateSlice';
import {
  CreateFolder,
  FolderListItem
} from '@business/workflows/ComponentUI/Photos/Folder';
import {
  useDeleteUserFoldersMutation,
  useGetUserFoldersQuery,
  useBulkDeleteUserFoldersMutation
} from '@ports/internal-services/folder/folderSlice';
import { useNavigate } from 'react-router-dom';
import { routePaths } from '@utils/constants/routePaths';
import { Filters } from '@business/workflows/ComponentUI/Filters';
import { getUserData } from '@ports/common/userStateSlice';
import { Table } from '@business/workflows/ComponentUI/Table/Table';
import { SearchInput } from '@business/workflows/ComponentUI/HierarchicalMultiSelect/search';
import { ContextMenu } from '@business/workflows/ComponentUI/ContextMenu';
import {
  getFileSizeString,
  handleDownload,
  handlePhotoClick
} from '@utils/helpers/lib/photoHelpers';
import { FilterSearchEmpty } from '@business/workflows/ComponentUI/FilterSearchEmpty';
import { resetFilterDetails } from '@ports/common/filtersStateSlice';
import { resetFolderDetails } from '@ports/common/folderStateSlice';
import { Checkbox } from '@business/workflows/ComponentUI/Checkbox';
import { getMonthDateFormat } from '@utils/helpers/lib';
import { NameCellRenderer } from '@business/workflows/ComponentUI/Photos/Photo';
import MediaInfoSidebar from './MediaInfoSidebar';
import MovePhotoModal from './MovePhotoModal';

const Photos = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const alertShowData = useSelector(getAlertState);
  const userDetails = useSelector(getUserData);
  const userNotificationData = useSelector(getUserNotificationData);
  const [selectedView, setSelectedView] = useState<'grid' | 'list'>('grid');
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([]);
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [photosCount, setPhotosCount] = useState<number>(0);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertData, setAlertData] = useState<AlertProps | null>(null);
  const [isOpenCreateFolder, setIsOpenCreateFolder] = useState<boolean>(false);
  const [selectedFolder, setSelectedFolder] = useState<any>(null);
  const [isDeleteConfirm, setIsDeleteConfirm] = useState<boolean>(false);
  const [isDeleteAssetConfirm, setIsDeleteAssetConfirm] =
    useState<boolean>(false);
  const [selectedAsset, setSelectedAsset] = useState<any>(null);
  const [isBulkDeleteConfirm, setIsBulkDeleteConfirm] =
    useState<boolean>(false);
  const [isBulkPrivateConfirm, setIsBulkPrivateConfirm] =
    useState<boolean>(false);
  const [isBulkDeleteFoldersConfirm, setIsBulkDeleteFoldersConfirm] =
    useState<boolean>(false);
  const [isMovePhotoModalOpen, setIsMovePhotoModalOpen] =
    useState<boolean>(false);
  const [assetToMove, setAssetToMove] = useState<string>('');
  const [isInfoSidebarOpen, setIsInfoSidebarOpen] = useState<boolean>(false);
  const [selectedAssetForInfo, setSelectedAssetForInfo] = useState<string>('');
  const [showCreateOptions, setShowCreateOptions] = useState<boolean>(false);
  const [filterOptions, setFilterOptions] = useState<any>({
    mediaType: ['image', 'video'],
    search: '',
    accessType: 'public'
  });
  const isFilterApplied = useMemo(
    () => Object.keys(filterOptions)?.length > 0,
    [filterOptions]
  );
  const createButtonRef = useRef<HTMLDivElement>(null);
  const [isAccessDropdownOpen, setIsAccessDropdownOpen] =
    useState<boolean>(false);
  const accessDropdownRef = useRef<HTMLDivElement>(null);
  const subscriberId = useSelector(getSubscriberId);
  const userId = userDetails?.userId || '';
  const [searchResults, setSearchResults] = useState<any>(null);
  const [search, setSearch] = useState<string>('');

  const { data: folderData, refetch: folderRefetch } = useGetUserFoldersQuery({
    limit: 4,
    offset: 0
  });

  const [DeleteFolder] = useDeleteUserFoldersMutation();
  const [BulkDeleteFolders] = useBulkDeleteUserFoldersMutation();
  const [DeleteAsset] = useDeleteAssetsMutation();
  const [AddFavorites] = useAddFavoritesMutation();
  const [AddPrivate] = useAddPrivateMutation();
  const [AssetSearch] = useAssetSearchMutation();
  const [BulkDeleteAssets] = useBulkDeleteAssetsMutation();
  const [BulkCreatePrivate] = useBulkCreatePrivateMutation();

  useEffect(() => {
    folderRefetch();
  }, []);

  const handlePhotoSelect = useCallback(
    (photoId: string) => {
      setSelectedPhotos(prev =>
        prev.includes(photoId)
          ? prev.filter(id => id !== photoId)
          : [...prev, photoId]
      );
      // Close folder selection when selecting photos
      if (selectedFolders.length > 0) {
        setSelectedFolders([]);
      }
    },
    [selectedFolders]
  );

  const handleSelectAll = () => {
    if (selectedPhotos.length === photosCount) {
      setSelectedPhotos([]);
    } else {
      setSelectedPhotos(searchResults?.rows.map((p: any) => p.id));
    }
  };

  const handleFolderSelect = (folderId: string) => {
    setSelectedFolders(prev =>
      prev.includes(folderId)
        ? prev.filter(id => id !== folderId)
        : [...prev, folderId]
    );
    // Close photo selection when selecting folders
    if (selectedPhotos.length > 0) {
      setSelectedPhotos([]);
    }
  };

  useEffect(() => {
    if (alertShowData?.scope === 'photo' && alertShowData.alert) {
      setShowAlert(true);
      setAlertData(alertShowData.alert);
    }
  }, [alertShowData]);

  useEffect(() => {
    // When filters applied & no results → do NOT show empty screen
    if (isFilterApplied && searchResults?.count === 0) {
      setIsModalOpen(false);
      return;
    }

    // When NO filters & no assets → show empty screen
    if (!isFilterApplied && searchResults?.count === 0) {
      setIsModalOpen(true);
    } else {
      setIsModalOpen(false);
    }
  }, [searchResults?.count, isFilterApplied]);

  const handleCreateFolder = () => {
    setShowCreateOptions(false);
    setSelectedFolder(null);
    setIsOpenCreateFolder(true);
  };

  const handleEditFolder = (folder: any) => {
    setSelectedFolder(folder);
    setIsOpenCreateFolder(true);
  };

  const handleDeleteFolder = (folder: any) => {
    setSelectedFolder(folder);
    setIsDeleteConfirm(true);
  };

  const handleUploadPhoto = () => {
    setShowCreateOptions(false);
    dispatch(setIsMediaUploadModalOpen(true));
  };

  const handleEmptyScreenUpload = () => {
    dispatch(setIsMediaUploadModalOpen(true));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        createButtonRef.current &&
        !createButtonRef.current.contains(event.target as Node)
      ) {
        setShowCreateOptions(false);
      }
      if (
        accessDropdownRef.current &&
        !accessDropdownRef.current.contains(event.target as Node)
      ) {
        setIsAccessDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const contextMenuItems = (folder: any) => [
    {
      id: '1',
      label: t('edit'),
      icon: <SVGAssets.ProfileEditIcon className="w-4 h-4 text-[#505050]" />,
      onClick: () => handleEditFolder(folder)
    },
    {
      id: '2',
      label: t('delete'),
      icon: <SVGAssets.TrashIcon className="w-4 h-4" />,
      onClick: () => handleDeleteFolder(folder)
    }
  ];

  const assetContextMenuItems = (asset: any) => [
    {
      id: '1',
      label: (
        <div className="flex items-center gap-2 px-4 py-2 hover:bg-gray-50 w-full cursor-pointer">
          <SVGAssets.Shares className="text-black" />
          <span className="text-sm">{t('photosPage.share')}</span>
        </div>
      ),
      onClick: () => {
        console.log('Share asset:', asset.id);
      }
    },
    {
      id: '2',
      label: (
        <div className="flex items-center gap-2 px-4 py-2 hover:bg-gray-50 w-full cursor-pointer">
          <SVGAssets.MoveTos />
          <span className="text-sm">{t('photosPage.moveTo')}</span>
        </div>
      ),
      onClick: () => {
        setAssetToMove(asset.id);
        setIsMovePhotoModalOpen(true);
      }
    },
    {
      id: '3',
      label: (
        <div className="flex items-center gap-2 px-4 py-2 hover:bg-gray-50 w-full cursor-pointer">
          <SVGAssets.InfoIcon className="w-5 h-5" />
          <span className="text-sm">{t('photosPage.info')}</span>
        </div>
      ),
      onClick: () => {
        setSelectedAssetForInfo(asset.id);
        setIsInfoSidebarOpen(true);
      }
    },
    {
      id: '4',
      label: (
        <div className="flex items-center gap-2 px-4 py-2 hover:bg-gray-50 w-full cursor-pointer">
          <SVGAssets.DownloadIcon className="w-5 h-5" />

          <span className="text-sm">{t('photosPage.download')}</span>
        </div>
      ),
      onClick: () => {
        if (asset.url) {
          handleDownload(asset.url);
        }
      }
    },
    {
      id: '5',
      label: (
        <div className="flex items-center gap-2 px-4 py-2 hover:bg-gray-50 w-full cursor-pointer">
          <SVGAssets.StarIcon className="w-5 h-5" />
          <span className="text-sm">{t('photosPage.favorite')}</span>
        </div>
      ),
      onClick: () => {
        handleToggleFavorite(asset.id, asset?.favorite?.status);
      }
    },
    {
      id: '6',
      label: (
        <div className="flex items-center gap-2 px-4 py-2 hover:bg-gray-50 w-full cursor-pointer">
          <SVGAssets.DiaryIcon className="w-5 h-5 text-black" />
          <span className="text-sm">{t('photosPage.diary')}</span>
        </div>
      ),
      onClick: () => {
        console.log('Add to diary:', asset.id);
      }
    },
    {
      id: '7',
      label: (
        <div className="flex items-center gap-2 px-4 py-2 hover:bg-gray-50 w-full cursor-pointer">
          {filterOptions?.accessType === 'public' ? (
            <>
              <SVGAssets.LockClosedIcon />
              <span className="text-sm">{t('photosPage.markAsPrivate')}</span>
            </>
          ) : (
            <>
              <SVGAssets.LockOpen />
              <span className="text-sm">{t('photosPage.markAsPublic')}</span>
            </>
          )}
        </div>
      ),
      onClick: () => {
        handleMarkAsPrivate(asset.id);
      }
    },
    {
      id: '8',
      label: (
        <div className="flex items-center gap-2 px-4 py-2 hover:bg-gray-50 text-red-600 w-full cursor-pointer">
          <SVGAssets.Delete className="w-4 h-4" />
          <span className="text-sm">{t('photosPage.delete')}</span>
        </div>
      ),
      onClick: () => {
        setSelectedAsset(asset);
        setIsDeleteAssetConfirm(true);
      }
    }
  ];

  const deleteAssetHandler = async (assetId: string) => {
    try {
      await DeleteAsset({ id: assetId }).unwrap();
      setShowAlert(true);
      setAlertData({
        type: 'success',
        message: t('photosPage.assetDeleteSuccess')
      });
      assetFiltersHandler();
    } catch (err: any) {
      setShowAlert(true);
      setAlertData({
        type: 'error',
        message: err?.message || t('photosPage.assetDeleteError')
      });
    } finally {
      setSelectedAsset(null);
    }
  };

  const handleToggleFavorite = useCallback(
    async (
      assetId: string,
      currentFavoriteStatus: string | undefined,
      e?: React.MouseEvent
    ) => {
      e?.stopPropagation();
      const isFavorite = currentFavoriteStatus === 'Active';
      const newStatus = isFavorite ? 'InActive' : 'Active';

      try {
        await AddFavorites({
          createFavorites: {
            subscriberId,
            userId,
            assetId,
            status: newStatus,
            createdBy: userId
          }
        }).unwrap();

        // Refetch assets to get updated favorite status
        assetFiltersHandler();

        setShowAlert(true);
        setAlertData({
          type: 'success',
          message: isFavorite
            ? t('photosPage.favoriteRemoved')
            : t('photosPage.favoriteSuccess')
        });
      } catch (err: any) {
        setShowAlert(true);
        setAlertData({
          type: 'error',
          message: err?.message || t('photosPage.favoriteError')
        });
      }
    },
    [subscriberId, userId]
  );

  const handleMarkAsPrivate = async (assetId: string) => {
    AddPrivate({
      createPrivate: {
        id: assetId,
        subscriberId,
        assetId,
        status: 'Active',
        createdBy: userId
      }
    })
      .then((res: any) => {
        setShowAlert(true);
        setAlertData({
          type: 'success',
          message: t(`messageCode.${res?.data?.data?.code}`)
        });
        assetFiltersHandler();
      })
      .catch((err: any) => {
        setShowAlert(true);
        setAlertData({
          type: 'error',
          message: err?.message || t('photosPage.privateError')
        });
      });
  };

  const handleBulkDelete = () => {
    if (selectedPhotos.length === 0) return;
    setIsBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    try {
      await BulkDeleteAssets({
        body: {
          assetIds: selectedPhotos
        }
      }).unwrap();

      setShowAlert(true);
      setAlertData({
        type: 'success',
        message: t('photosPage.bulkDeleteSuccess')
      });
      setSelectedPhotos([]);
      setIsBulkDeleteConfirm(false);
      assetFiltersHandler();
    } catch (err: any) {
      setShowAlert(true);
      setAlertData({
        type: 'error',
        message: err?.message || t('photosPage.bulkDeleteError')
      });
      setIsBulkDeleteConfirm(false);
    }
  };

  const handleBulkPrivate = () => {
    if (selectedPhotos.length === 0) return;
    setIsBulkPrivateConfirm(true);
  };

  const confirmBulkPrivate = async () => {
    await BulkCreatePrivate({
      body: {
        assetIds: selectedPhotos,
        subscriberId
      }
    })
      .then((res: any) => {
        setShowAlert(true);
        setAlertData({
          type: 'success',
          message: t(`messageCode.${res?.data?.data?.code}`)
        });
        setSelectedPhotos([]);
        setIsBulkPrivateConfirm(false);
        assetFiltersHandler();
      })
      .catch((err: any) => {
        setShowAlert(true);
        setAlertData({
          type: 'error',
          message: err?.message || t('photosPage.bulkPrivateError')
        });
        setIsBulkPrivateConfirm(false);
      });
  };

  const handleBulkDeleteFolders = () => {
    if (selectedFolders.length === 0) return;
    setIsBulkDeleteFoldersConfirm(true);
  };

  const confirmBulkDeleteFolders = async () => {
    try {
      await BulkDeleteFolders({
        body: {
          folderIds: selectedFolders
        }
      }).unwrap();

      setShowAlert(true);
      setAlertData({
        type: 'success',
        message: t('photosPage.bulkDeleteFoldersSuccess')
      });
      setSelectedFolders([]);
      setIsBulkDeleteFoldersConfirm(false);
      folderRefetch();
    } catch (err: any) {
      setShowAlert(true);
      setAlertData({
        type: 'error',
        message: err?.message || t('photosPage.bulkDeleteFoldersError')
      });
      setIsBulkDeleteFoldersConfirm(false);
    }
  };

  const deleteFolderHandler = (folderId: string) => {
    const dataObj = {
      id: folderId
    };
    DeleteFolder(dataObj)
      .then((res: any) => {
        if (res?.data) {
          setShowAlert(true);
          setAlertData({
            type: 'success',
            message: t('photosPage.deleteSuccess')
          });
          folderRefetch(); // Add refetch after successful deletion
        } else {
          setShowAlert(true);
          setAlertData({
            type: 'error',
            message: t('photosPage.deleteError')
          });
        }
      })
      .catch(err => {
        setShowAlert(true);
        setAlertData({
          type: 'error',
          message: err?.message
        });
      })
      .finally(() => {
        setSelectedFolder(null);
      });
  };

  const columns = useMemo(() => {
    return [
      {
        header: 'Name',
        accessorKey: 'fileName',
        cell: ({ row }: any) => {
          const photo = row.original;
          const isChecked = selectedPhotos.includes(photo?.id);

          return (
            <NameCellRenderer
              photo={photo}
              isChecked={isChecked}
              handlePhotoSelect={handlePhotoSelect}
              handleToggleFavorite={handleToggleFavorite}
            />
          );
        }
      },
      {
        header: 'Date created',
        accessorKey: 'createdAt',
        cell: ({ getValue }: any) => {
          const value = getValue();
          if (!value) return <span className="text-sm text-gray-500">-</span>;
          const date = new Date(value);
          return (
            <span className="text-sm text-gray-500">
              {date.toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric'
              })}
            </span>
          );
        }
      },
      {
        header: 'Interactions',
        accessorKey: 'interactions',
        cell: ({ row }: any) => {
          return (
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <span className="flex items-center space-x-1">
                <SVGAssets.LikeIcons className="w-4 h-4 ml-2 !text-[#505050]" />{' '}
                <span>{row?.original?.likes?.length}</span>
              </span>
              <span className="flex items-center space-x-1">
                <SVGAssets.CommentIcons className="w-4 h-4 ml-2 !text-[#505050]" />{' '}
                <span>{row?.original?.comments?.length}</span>
              </span>
            </div>
          );
        }
      },
      {
        header: 'Storage',
        accessorKey: 'size',
        cell: ({ getValue }: any) => {
          const value = getValue();
          let formattedValue = '-';
          if (value) {
            formattedValue = getFileSizeString(value);
          }
          return (
            <span className="text-sm text-gray-500">{formattedValue}</span>
          );
        }
      },
      {
        header: 'Last viewed',
        accessorKey: 'assetViewDetail',
        cell: ({ row }: any) => {
          const updatedAt = row?.original?.assetViewDetail?.updatedAt;
          let formattedDate: ReactNode = '-';
          if (updatedAt) {
            formattedDate = getMonthDateFormat(updatedAt);
          }
          return <span className="text-sm text-gray-500">{formattedDate}</span>;
        }
      },
      {
        header: '',
        accessorKey: 'actions',
        cell: ({ row }: any) => {
          const asset = row.original;
          return (
            <div className="relative" style={{ overflow: 'visible' }}>
              <ContextMenu
                menuItems={assetContextMenuItems(asset)}
                wrapperClass="!static"
                itemContainerClass="!fixed !w-48 !shadow-xl !border !border-gray-200 !py-1 !max-h-none !z-[9999]"
                icon={
                  <button className="p-1 hover:bg-gray-100 rounded">
                    <SVGAssets.ThreeDots className="w-5 h-5 text-gray-400" />
                  </button>
                }
              />
            </div>
          );
        }
      }
    ];
  }, [selectedPhotos]);

  const assetFiltersHandler = (assetId?: string) => {
    // Handle sortBy logic
    let sortBy = 'createdAt';
    let orderBy = 'desc'; // default to most recent

    if (filterOptions?.sortBy) {
      const sortValue = filterOptions.sortBy;
      if (sortValue === 'mostRecent') {
        sortBy = 'createdAt';
        orderBy = 'desc';
      } else if (sortValue === 'leastRecent') {
        sortBy = 'createdAt';
        orderBy = 'asc';
      }
    }
    const dataObj = {
      assetSearch: {
        search: filterOptions?.search,
        sortBy: sortBy,
        orderBy: orderBy,
        favorites: filterOptions?.favorites || false,
        sharedWith: filterOptions?.sharedWith || '',
        people: filterOptions?.people || '',
        things: filterOptions?.things || '',
        locations: filterOptions?.locations || '',
        dates: filterOptions?.date || '',
        interactions: filterOptions?.interactions || '',
        mediaType:
          filterOptions?.mediaType?.length > 0
            ? filterOptions?.mediaType
            : ['image', 'video'],
        access: filterOptions?.accessType === 'public' ? 'public' : 'private',
        assetId
      }
    };
    AssetSearch(dataObj)
      .then((res: any) => {
        if (res?.data) {
          if (assetId) {
            setSearchResults((prev: any) => {
              if (!prev) return res?.data?.data;
              if (
                res?.data?.data?.rows?.findIndex(
                  (a: any) => a.id === assetId
                ) !== -1
              ) {
                const updatedAsset = {
                  ...prev,
                  rows: [...prev.rows],
                  count: prev.count
                };
                if (orderBy === 'desc') {
                  updatedAsset.rows.unshift(
                    res?.data?.data?.rows.find((a: any) => a.id === assetId)
                  );
                } else {
                  updatedAsset.rows.push(
                    res?.data?.data?.rows.find((a: any) => a.id === assetId)
                  );
                }
                updatedAsset.count += 1;
                return updatedAsset;
              }
              return prev;
            });
            setPhotosCount(prev => prev + 1);
          } else {
            setSearchResults(res?.data?.data);
            setPhotosCount(res?.data?.data?.totalCount);
          }
        } else {
          setShowAlert(true);
          setAlertData({
            type: 'error',
            message: t('applyError')
          });
        }
      })
      .catch(err => {
        setShowAlert(true);
        setAlertData({
          type: 'error',
          message: err?.message
        });
      });
  };

  // Centralized effect to handle assetFiltersHandler call
  useEffect(() => {
    setSelectedPhotos([]);
    setSelectedFolders([]);
    assetFiltersHandler();
  }, [filterOptions]);

  useEffect(() => {
    if (
      userNotificationData?.page === 'photos' &&
      userNotificationData?.notificationData?.assetId
    ) {
      assetFiltersHandler(userNotificationData?.notificationData?.assetId);
    }
  }, [userNotificationData]);

  // Only update filterOptions.search when search changes and value is different
  useEffect(() => {
    const handler = setTimeout(() => {
      setFilterOptions((prev: any) => {
        if (prev.search === search) return prev;
        return {
          ...prev,
          search: search
        };
      });
    }, 400); // 400ms debounce
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    dispatch(resetFilterDetails());
    dispatch(resetFolderDetails());
  }, []);

  useEffect(() => {
    if (searchResults?.totalCount > 0) {
      dispatch(setIsMediaUploadModalOpen(false));
    } else {
      dispatch(setIsMediaUploadModalOpen(true));
    }
  }, [searchResults?.totalCount]);

  const handleRowClick = (photo: any) => {
    handlePhotoClick(
      photo,
      navigate,
      dispatch,
      setSelectedMedia,
      routePaths.MediaView
    );
  };

  return (
    <BaseAppLayout>
      {searchResults?.totalCount > 0 ? (
        <div className="flex flex-col h-full">
          {/* Selection Bar - for both Photos and Folders */}
          {selectedPhotos.length > 0 && (
            <SelectionBar
              selectedCount={selectedPhotos.length}
              itemType="photo"
              actions={[
                {
                  icon:
                    filterOptions?.accessType === 'public' ? (
                      <SVGAssets.LockClosedIcon className="w-5 h-5" />
                    ) : (
                      <SVGAssets.LockOpen className="w-5 h-5" />
                    ),
                  onClick: handleBulkPrivate,
                  title:
                    filterOptions?.accessType === 'public'
                      ? t('photosPage.markAsPrivate')
                      : t('photosPage.markAsPublic')
                },
                {
                  icon: (
                    <SVGAssets.TrashIcon className="w-5 h-5 text-red-600" />
                  ),
                  onClick: handleBulkDelete,
                  title: t('photosPage.delete')
                }
              ]}
            />
          )}
          {selectedFolders.length > 0 && (
            <SelectionBar
              selectedCount={selectedFolders.length}
              itemType="folder"
              actions={[
                {
                  icon: (
                    <SVGAssets.TrashIcon className="w-5 h-5 text-red-600" />
                  ),
                  onClick: handleBulkDeleteFolders,
                  title: t('delete')
                }
              ]}
            />
          )}

          {/* Add padding when selection bar is visible */}
          <div
            className={
              selectedPhotos.length > 0 || selectedFolders.length > 0
                ? 'pt-16'
                : ''
            }>
            <div className="items-center justify-between relative">
              <h1 className="text-2xl font-bold text-[#353535] pb-3">
                {t('photos')}
              </h1>
              {showAlert && (
                <div className="absolute right-0 top-0">
                  <Alert
                    onClose={() => {
                      dispatch(resetAlertState());
                      setShowAlert(false);
                    }}
                    {...alertData}
                    autoHide={true}
                  />
                </div>
              )}

              <div className="relative" ref={createButtonRef}>
                <Button
                  btnStyle="primary"
                  type="button"
                  onClick={() => setShowCreateOptions(!showCreateOptions)}
                  className=" rounded-lg px-3 py-3 font-semibold text-sm flex text-center justify-center">
                  <SVGAssets.PlusRounded className="w-5 h-5 mr-2" />
                  {t('create')}
                </Button>
                {showCreateOptions && (
                  <CreateOptions
                    onCreateFolder={handleCreateFolder}
                    onUploadPhoto={handleUploadPhoto}
                  />
                )}
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 py-4" style={{ overflow: 'visible' }}>
              {/* Folders Section */}
              {folderData?.data?.count > 0 && (
                <>
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-gray-800">
                        {t('photosPage.folders')}
                      </h2>
                      {folderData?.data?.count > 4 && (
                        <Button
                          btnStyle="link"
                          onClick={() => navigate(routePaths.Folders)}
                          className="text-sm font-semibold text-[#49712B] hover:underline">
                          {t('viewAll')}
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 3xl:grid-cols-6 gap-4">
                      {folderData?.data?.rows?.map(folder => (
                        <FolderListItem
                          key={folder?.id}
                          folderId={folder?.id}
                          folderName={folder?.name}
                          fileCount={folder?.filesCount}
                          contextMenuItems={contextMenuItems(folder)}
                          isSelected={selectedFolders.includes(folder?.id)}
                          onSelect={handleFolderSelect}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Filter Section */}
              <div className="mb-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  {t('photosPage.filterBy')}
                </h3>
                <Filters
                  setFilterOptions={setFilterOptions}
                  selectedView={selectedView}
                  onViewChange={setSelectedView}
                  filterOptions={filterOptions}
                />
                <SearchInput
                  className="mt-5"
                  value={search || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSearch(e.target.value);
                  }}
                  onCameraClick={() => {
                    console.log('test');
                  }}
                />
              </div>

              {/* Photos Section */}
              <div style={{ overflow: 'visible' }}>
                {/* Photos heading and Access Dropdown */}
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-800">
                    {t('photos')}
                  </h2>
                  <div className="relative" ref={accessDropdownRef}>
                    <Button
                      onClick={() =>
                        setIsAccessDropdownOpen(!isAccessDropdownOpen)
                      }
                      btnStyle="no-outline"
                      className="px-3 py-2 text-sm bg-white text-gray-700 rounded-md focus:outline-none cursor-pointer  hover:bg-gray-50 flex items-center gap-2 min-w-[120px] justify-between">
                      <div className="flex items-center gap-2">
                        {filterOptions?.accessType === 'private' ? (
                          <SVGAssets.LockClosedIcon />
                        ) : (
                          <SVGAssets.LockOpen />
                        )}
                        <span>
                          {filterOptions?.accessType === 'public'
                            ? t('photosPage.public')
                            : t('photosPage.private')}
                        </span>
                      </div>
                      <svg
                        className={`w-4 h-4 transition-transform ${isAccessDropdownOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </Button>

                    {isAccessDropdownOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                        <div className="py-1">
                          <button
                            onClick={() => {
                              setFilterOptions((prev: any) => ({
                                ...prev,
                                accessType: 'public'
                              }));
                              setIsAccessDropdownOpen(false);
                            }}
                            className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2 ${
                              filterOptions?.accessType === 'public'
                                ? 'bg-gray-50'
                                : ''
                            }`}>
                            <SVGAssets.LockOpen />
                            <span>{t('photosPage.public')}</span>
                          </button>
                          <button
                            onClick={() => {
                              setFilterOptions((prev: any) => ({
                                ...prev,
                                accessType: 'private'
                              }));
                              setIsAccessDropdownOpen(false);
                            }}
                            className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2 ${
                              filterOptions?.accessType === 'private'
                                ? 'bg-gray-50'
                                : ''
                            }`}>
                            <SVGAssets.LockClosedIcon />

                            <span>{t('photosPage.private')}</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  {false && (
                    <div className="flex items-center gap-3">
                      <Button
                        onClick={handleSelectAll}
                        className="text-sm text-[#7F9D48] hover:underline"
                        btnStyle="no-outline">
                        {selectedPhotos.length === photosCount
                          ? t('photosPage.deselectAll')
                          : t('photosPage.selectAll')}
                      </Button>
                      <div className="flex gap-1 border border-gray-300 rounded-md p-1">
                        <Button
                          btnStyle="link"
                          onClick={() => setSelectedView('grid')}
                          className={`p-1.5 rounded ${selectedView === 'grid' ? 'bg-gray-200' : 'hover:bg-gray-100'}`}>
                          <SVGAssets.GridIcon className="w-4 h-4" />
                        </Button>
                        <Button
                          btnStyle="link"
                          onClick={() => setSelectedView('list')}
                          className={`p-1.5 rounded ${selectedView === 'list' ? 'bg-gray-200' : 'hover:bg-gray-100'}`}>
                          <SVGAssets.ListIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                {searchResults?.count > 0 ? (
                  <div>
                    {selectedView === 'grid' ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                        {searchResults?.rows.map(
                          (photo: any, index: number) => (
                            <div
                              key={photo.id || index}
                              className="relative group cursor-pointer rounded-lg overflow-visible bg-white border border-gray-200 hover:shadow-lg transition-shadow"
                              onClick={() => handleRowClick(photo)}>
                              <div className="aspect-square overflow-hidden rounded-lg">
                                <img
                                  src={
                                    photo?.thumbNailUrl ||
                                    '/placeholder-image.jpg'
                                  }
                                  alt={photo?.fileName || `Photo ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                                {/* Play icon for videos */}
                                {photo?.type?.startsWith('video/') && (
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-16 h-16 bg-black/60 rounded-full flex items-center justify-center">
                                      <svg
                                        className="w-8 h-8 text-white ml-1"
                                        fill="currentColor"
                                        viewBox="0 0 24 24">
                                        <path d="M8 5v14l11-7z" />
                                      </svg>
                                    </div>
                                  </div>
                                )}
                              </div>

                              <div className="absolute top-2 left-2 z-[5] flex items-center gap-2">
                                <Checkbox
                                  name={`${photo.id}`}
                                  checked={selectedPhotos.includes(photo.id)}
                                  onChange={e => {
                                    e.stopPropagation();
                                    handlePhotoSelect(photo.id);
                                  }}
                                  onClick={e => e.stopPropagation()}
                                />
                                <button
                                  onClick={e =>
                                    handleToggleFavorite(
                                      photo.id,
                                      photo?.favorite?.status,
                                      e
                                    )
                                  }
                                  className={`w-8 h-8 rounded-full shadow-md flex items-center justify-center hover:bg-gray-100 hover-star ${
                                    photo?.favorite?.status === 'Active'
                                      ? 'hover-star-active'
                                      : ''
                                  }`}>
                                  <SVGAssets.FavoriteIcons className="w-5 h-5" />
                                </button>
                              </div>

                              <div className="absolute bottom-2 right-2 z-[5] flex items-center gap-2">
                                <div className="flex items-center gap-1  rounded-full px-2 py-1">
                                  <SVGAssets.CommentIcons className="w-4 h-4 text-white" />
                                  <span className="text-white text-xs font-medium">
                                    {photo?.comments?.length || 0}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1  rounded-full px-2 py-1">
                                  <SVGAssets.LikeIcons className="w-4 h-4 text-white" />
                                  <span className="text-white text-xs font-medium">
                                    {photo?.likes?.length || 0}
                                  </span>
                                </div>
                              </div>

                              <div className="absolute top-2 right-2 transition-opacity z-[100]">
                                <div className="relative">
                                  <ContextMenu
                                    menuItems={assetContextMenuItems(photo)}
                                    wrapperClass="!static"
                                    itemContainerClass="!absolute !right-0 !top-full !mt-2 !w-48 !z-[100] !py-1 !max-h-none"
                                    icon={
                                      <div className="w-8 h-8 group-hover:shadow-md group-hover:bg-white group-hover:text-black text-white rounded-full  flex items-center justify-center hover:bg-gray-100">
                                        <SVGAssets.ThreeDots className="w-4 h-4" />
                                      </div>
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    ) : (
                      <div className="w-full" style={{ overflow: 'visible' }}>
                        <Table
                          tableColumns={columns}
                          tableData={searchResults?.rows ?? []}
                          className="w-full"
                          onRowClick={handleRowClick}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <FilterSearchEmpty />
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <EmptyScreen
            onButtonClick={handleEmptyScreenUpload}
            icon={<SVGAssets.CameraIcon className="w-32 h-32 text-[#7F9D48]" />}
            title={t('dashboard.uploadPhotosTitle')}
            description={t('dashboard.uploadPhotosDescription')}
            btnName={t('dashboard.uploadPhotosButton')}
            feature="photos"
          />

          {isModalOpen && (
            <ConfirmationBox
              isOpen={isModalOpen}
              onCancel={() => setIsModalOpen(false)}
              onClose={() => setIsModalOpen(false)}
              onConfirm={() => {
                setIsModalOpen(false);
                dispatch(setIsMediaUploadModalOpen(true));
              }}
              headerTitle={t('galleryAccess.headerTitle')}
              title={t('galleryAccess.title')}
              description={t('galleryAccess.description')}
              icon={
                <SVGAssets.UploadModalIcon className="w-20 h-20 text-[#7F9D48]" />
              }
              confirmText={t('galleryAccess.confirmText')}
              cancelText={t('galleryAccess.cancelText')}
            />
          )}
        </>
      )}

      {/* Move delete confirmation outside conditional - always available */}
      <ConfirmationBox
        isOpen={isDeleteConfirm}
        onCancel={() => {
          setIsDeleteConfirm(false);
          setSelectedFolder(null);
        }}
        onClose={() => {
          setIsDeleteConfirm(false);
          setSelectedFolder(null);
        }}
        onConfirm={() => {
          setIsDeleteConfirm(false);
          deleteFolderHandler(selectedFolder?.id);
        }}
        headerTitle={t('photosPage.deleteFolder')}
        title={t('photosPage.deleteConfirmTitle')}
        description={t('photosPage.deleteConfirmDescription')}
        icon={<SVGAssets.TrashIcon className="w-20 h-20 text-[#7F9D48]" />}
        confirmText={t('deleteIt')}
        cancelText={t('keepIt')}
      />

      {/* CreateFolder always available */}
      <CreateFolder
        isOpen={isOpenCreateFolder}
        onClose={() => {
          setIsOpenCreateFolder(false);
          setSelectedFolder(null);
        }}
        folderData={selectedFolder}
        folderSearchHandler={() => {
          folderRefetch();
          assetFiltersHandler();
        }}
      />

      {/* Delete Asset Confirmation */}
      <ConfirmationModal
        isOpen={isDeleteAssetConfirm}
        onClose={() => {
          setIsDeleteAssetConfirm(false);
          setSelectedAsset(null);
        }}
        onConfirm={() => {
          setIsDeleteAssetConfirm(false);
          deleteAssetHandler(selectedAsset?.id);
        }}
        variant="delete"
      />

      {/* Bulk Delete Confirmation */}
      <ConfirmationModal
        isOpen={isBulkDeleteConfirm}
        onClose={() => setIsBulkDeleteConfirm(false)}
        onConfirm={confirmBulkDelete}
        variant="delete"
      />

      {/* Bulk Private Confirmation */}
      {isBulkPrivateConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-800">
                {filterOptions?.accessType === 'public'
                  ? t('photosPage.markAsPrivate')
                  : t('photosPage.markAsPublic')}
              </h2>
              <Button
                onClick={() => setIsBulkPrivateConfirm(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                aria-label="Close"
                btnStyle="no-outline">
                ×
              </Button>
            </div>

            {/* Content */}
            <div className="p-6 text-center">
              <div className="mb-4 flex justify-center">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                  {filterOptions?.accessType === 'public' ? (
                    <SVGAssets.LockClosedIcon className="w-12 h-12 text-green-600" />
                  ) : (
                    <SVGAssets.LockOpen className="w-12 h-12 text-green-600" />
                  )}
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {filterOptions?.accessType === 'public'
                  ? t('photosPage.bulkPrivateTitle')
                  : t('photosPage.bulkPublicTitle')}
              </h3>
              <p className="text-sm text-gray-500">
                {filterOptions?.accessType === 'public'
                  ? t('photosPage.bulkPrivateMessage')
                  : t('photosPage.bulkPublicMessage')}
              </p>
            </div>

            {/* Footer */}
            <div className="flex gap-3 p-4 border-t">
              <Button
                onClick={() => setIsBulkPrivateConfirm(false)}
                btnStyle="no-outline"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors">
                {t('cancel')}
              </Button>
              <Button
                onClick={confirmBulkPrivate}
                btnStyle="no-outline"
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors">
                {filterOptions?.accessType === 'public'
                  ? t('photosPage.yesMarkPrivate')
                  : t('photosPage.yesMarkPublic')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation */}
      <ConfirmationModal
        isOpen={isBulkDeleteConfirm}
        onClose={() => setIsBulkDeleteConfirm(false)}
        onConfirm={confirmBulkDelete}
        variant="delete"
      />

      {/* Bulk Delete Folders Confirmation - Reuse existing ConfirmationBox */}
      <ConfirmationBox
        isOpen={isBulkDeleteFoldersConfirm}
        onCancel={() => setIsBulkDeleteFoldersConfirm(false)}
        onClose={() => setIsBulkDeleteFoldersConfirm(false)}
        onConfirm={confirmBulkDeleteFolders}
        headerTitle={t('photosPage.deleteFolder')}
        title={t('photosPage.deleteConfirmTitle')}
        description={t('photosPage.deleteConfirmDescription')}
        icon={<SVGAssets.Delete className="w-20 h-20 pt-3" />}
        confirmText={t('deleteIt')}
        cancelText={t('keepIt')}
      />

      {/* Move Photo Modal */}
      <MovePhotoModal
        isOpen={isMovePhotoModalOpen}
        onClose={() => {
          setIsMovePhotoModalOpen(false);
          setAssetToMove('');
        }}
        assetId={assetToMove}
        onSuccess={() => {
          setShowAlert(true);
          setAlertData({
            type: 'success',
            message: t('photosPage.moveSuccess')
          });
          assetFiltersHandler();
        }}
      />

      {/* Media Info Sidebar */}
      <MediaInfoSidebar
        isOpen={isInfoSidebarOpen}
        onClose={() => {
          setIsInfoSidebarOpen(false);
          setSelectedAssetForInfo('');
        }}
        onReopen={() => {
          setIsInfoSidebarOpen(true);
        }}
        assetId={selectedAssetForInfo}
      />
    </BaseAppLayout>
  );
};

export default Photos;
